import { datadogRum } from '@datadog/browser-rum';
import { useContext, useEffect } from 'react';
import GlobalContext from '~/context/vdp/GlobalContext';
import { useGetVehicleDetails } from '~/context/vdp/VehicleContext';
import { useSendNativeDeviceTypeToDD } from '~/hooks/common';
import { usePriceDrop } from '~/hooks/vdp';
import useAlertStatus from '~/hooks/vdp/selectors/useAlertStatus';
import useSendSptEvent from '~/hooks/vdp/useSendSptEventAndActions';
import { BrowserCookies } from '~/utilities';
import {
  useSendAvailabilityData,
  useSendContentAvailability,
  useSendDeliveryMiles,
  useSendDeliveryPickup,
  useSendSoftPull,
  useSendTransitCharge,
  useSendVehiclesAvailableSooner,
  useSendZipCodeChanges
} from './events';

export default function useSendDataEvents() {
  const {
    purchasePending,
    unavailable,
    stopSaleRecall,
    outOfMarket,
    isSold,
    notAvailableInCA
  } = useAlertStatus();
  const { experimentsLoaded, experimentsWithBucket } =
    useContext(GlobalContext);
  const { inventoryType, stores, vehicleId } = useGetVehicleDetails();
  const { sendPriceDropEvent } = usePriceDrop();

  useEffect(() => {
    if (!experimentsLoaded) return;
    datadogRum.setGlobalContextProperty('experiments', experimentsWithBucket);
    datadogRum.setGlobalContextProperty('inventoryType', inventoryType);
    datadogRum.setGlobalContextProperty('stores', stores);
    datadogRum.setGlobalContextProperty('Geolocation', {
      CVCurrentState: BrowserCookies.get('CVCurrentState'),
      CVCurrentZip: BrowserCookies.get('CVCurrentZip')
    });
  }, [experimentsLoaded, experimentsWithBucket, inventoryType, stores]);

  useSendNativeDeviceTypeToDD();
  useSendAvailabilityData();
  useSendContentAvailability();
  useSendDeliveryMiles();
  useSendDeliveryPickup();
  useSendSoftPull();
  useSendSptEvent();
  useSendTransitCharge();
  useSendVehiclesAvailableSooner();
  useSendZipCodeChanges();

  useEffect(() => {
    if (!experimentsLoaded) return;
    sendPriceDropEvent();
  }, [experimentsLoaded, sendPriceDropEvent]);

  useEffect(() => {
    const alerts = [
      { type: purchasePending, description: 'vehicle-purchase-pending' },
      { type: unavailable, description: 'vehicle-unavailable' },
      { type: stopSaleRecall, description: 'vehicle-stop-sale-recall' },
      { type: outOfMarket, description: 'vehicle-out-of-market' },
      { type: isSold, description: 'vehicle-is-sold' },
      { type: notAvailableInCA, description: 'vehicle-not-available-in-ca' }
    ];

    alerts.forEach(alert => {
      if (alert.type)
        datadogRum.addAction(`vdp-alert-${alert.description}`, { vehicleId });
    });
  }, [
    isSold,
    notAvailableInCA,
    outOfMarket,
    purchasePending,
    stopSaleRecall,
    unavailable,
    vehicleId
  ]);
}
